/**LAGOSSDGContribution — /state/lagos/sdg — Upgraded with strength indicators */
import React from "react";
import { useLagos } from "@/context/LAGOSContext";

const SDG_COLORS = { 7:"#FCC30B", 8:"#A21942", 9:"#FD6925", 11:"#FD9D24", 12:"#BF8B2E", 13:"#3F7E44" };
const fmtN = (v, u="") => { if (!v && v!==0) return "—"; const n=Number(v); if (n>=1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n>=1e3) return `${(n/1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };

const SDG_TARGETS = {
  7:  { target: "7.2",  desc: "Affordable & Clean Energy",          oecd: "Rio Marker 2 — Principal" },
  8:  { target: "8.4",  desc: "Decent Work & Economic Growth",       oecd: "Rio Marker 2 — Significant" },
  9:  { target: "9.4",  desc: "Industry, Innovation & Infrastructure",oecd: "Rio Marker 2 — Significant" },
  11: { target: "11.6", desc: "Sustainable Cities & Communities",    oecd: "Rio Marker 2 — Principal" },
  12: { target: "12.5", desc: "Responsible Consumption & Production", oecd: "Rio Marker 2 — Principal" },
  13: { target: "13.2", desc: "Climate Action",                      oecd: "Rio Marker 2 — Principal" },
};

function StrengthBar({ relevance }) {
  const strength = relevance === "High" ? 100 : 65;
  const color = relevance === "High" ? "#059669" : "#d97706";
  return (
    <div className="flex items-center gap-2 mt-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: strength + "%", background: color }} />
      </div>
      <span className="text-[10px] font-bold" style={{ color }}>{relevance}</span>
    </div>
  );
}

export default function LAGOSSDGContribution() {
  const { sdg, loading, portfolio } = useLagos();
  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading SDG data…</div>;

  const highCount = sdg.filter(s => s.relevance === "High").length;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">

      {/* Header */}
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
          Lagos State Government
        </span>
        <h1 className="text-2xl font-black text-slate-900 mt-1.5">SDG Contribution</h1>
        <p className="text-xs text-slate-400 mt-0.5">
          Derived dynamically from {portfolio.total_entities || 0} entity portfolio · {sdg.length} SDGs addressed · {highCount} with High relevance
        </p>
      </div>

      {/* Portfolio-level ESG summary */}
      <div className="bg-[#0f172a] rounded-xl p-5 text-white">
        <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-3">Portfolio ESG / OECD Rio Marker Summary</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ["SDGs Addressed",         String(sdg.length),                          "#94a3b8"],
            ["High Relevance SDGs",    String(highCount),                            "#34d399"],
            ["Rio Marker 2 — Principal", "SDGs 7, 11, 12, 13",                      "#fbbf24"],
            ["OECD GBP Climate Finance", "Eligible — mitigation principal",          "#60a5fa"],
          ].map(([k, v, c]) => (
            <div key={k}>
              <p className="text-[9px] opacity-40 uppercase tracking-wider">{k}</p>
              <p className="text-sm font-black mt-0.5" style={{ color: c }}>{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* SDG Cards */}
      <div className="grid grid-cols-2 gap-4">
        {sdg.map(s => {
          const meta = SDG_TARGETS[s.id] || {};
          const bgColor = SDG_COLORS[s.id] || "#64748b";
          return (
            <div key={s.id} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden" data-testid={`sdg-${s.id}`}>
              {/* SDG header band */}
              <div className="flex items-center gap-4 p-4 pb-3" style={{ borderLeft: `4px solid ${bgColor}` }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-lg font-black flex-shrink-0" style={{ background: bgColor }}>
                  {s.id}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-black text-slate-800">SDG {s.id} — {s.name}</p>
                  <p className="text-[10px] text-slate-400">Target {meta.target || "—"} · {meta.oecd || "—"}</p>
                </div>
              </div>

              <div className="px-4 pb-4 space-y-3">
                {/* Contribution strength */}
                <StrengthBar relevance={s.relevance} />

                {/* Quantified contribution */}
                {s.quantified_value != null && (
                  <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-slate-500">{s.unit}</p>
                    <p className="text-lg font-black text-slate-900">{fmtN(s.quantified_value)}</p>
                  </div>
                )}

                {/* Driver */}
                <p className="text-xs text-slate-500 leading-relaxed">{s.driver}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* OECD / ESG note */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-xs text-emerald-700">
        <strong>OECD Rio Marker 2:</strong> All platform activities qualify as climate finance under Rio Marker 2 — mitigation principal objective.
        SDG data is derived dynamically from the Lagos entity portfolio using the DCI-LAGOS-V1.0 SSOT engine. Values update automatically with any entity modification.
      </div>
    </div>
  );
}
